import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Reachability, ReachabilityType } from './reachabilities.model';
import { environment } from '../../../environments/environment';
/** Raw HAL envelope from the reachabilities endpoint */
interface ReachabilityHalResponse {
  _embedded?: { reachabilityDataList?: RawReachability[] };
}

/** Raw reachability shape from the API (before mapping) */
interface RawReachability {
  reachabilityId?: number;
  id?: number;
  name: string;
  destination: string;
  type: ReachabilityType;
  makePublicOnRequest?: boolean;
  publiclyAvailable?: boolean;
  callNotificationEmailFormat: string | null;
}

@Injectable({ providedIn: 'root' })
export class ReachabilitiesService {
  private readonly http = inject(HttpClient);

  private basePath(psId: number): string {
    return `${environment.phoneServiceUrl}/phoneservices/${psId}/reachabilities`;
  }

  getAll(psId: number): Observable<Reachability[]> {
    return this.http.get<ReachabilityHalResponse>(this.basePath(psId)).pipe(
      map(res => {
        const list = res?._embedded?.reachabilityDataList ?? [];
        return Array.isArray(list) ? list.map(this.mapReachability) : [];
      }),
    );
  }

  private mapReachability(r: RawReachability): Reachability {
    return {
      id: r.reachabilityId ?? r.id ?? 0,
      name: r.name,
      destination: r.destination,
      type: r.type,
      publiclyAvailable: r.makePublicOnRequest ?? r.publiclyAvailable ?? false,
      callNotificationEmailFormat: r.callNotificationEmailFormat,
    };
  }

  create(psId: number, reachability: Partial<Reachability>): Observable<Reachability> {
    // The API returns the RAW shape (`reachabilityId`, `makePublicOnRequest`, no
    // `id`) — map it like getAll() so callers get a populated `id`. Without this,
    // `created.id` is undefined and "Create & Add" POSTs `{ reachability: {} }`
    // → BE 400/500 (EB-4148).
    return this.http.post<RawReachability>(this.basePath(psId), this.toRequestBody(reachability)).pipe(
      map(r => this.mapReachability(r)),
    );
  }

  update(psId: number, id: number, reachability: Partial<Reachability>): Observable<Reachability> {
    return this.http.put<RawReachability>(`${this.basePath(psId)}/${id}`, this.toRequestBody(reachability)).pipe(
      map(r => this.mapReachability(r)),
    );
  }

  /**
   * Translate the FE model's `publiclyAvailable` to the API's request field
   * `makePublicOnRequest`. The phoneservice request schema has NO
   * `publiclyAvailable` property (verified via OpenAPI + live probe on test) —
   * Spring silently drops the unknown field, so the operator's "Publicly
   * available" choice never persists on create or update. This is the request-side
   * mirror of the `makePublicOnRequest → publiclyAvailable` translation
   * mapReachability() applies to responses (EB-4148).
   */
  private toRequestBody(reachability: Partial<Reachability>): Record<string, unknown> {
    const { publiclyAvailable, ...rest } = reachability;
    return publiclyAvailable === undefined
      ? { ...rest }
      : { ...rest, makePublicOnRequest: publiclyAvailable };
  }

  delete(psId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath(psId)}/${id}`);
  }
}
